// src/data/packagesData.ts
export type PricingTier = {
  group: string; // e.g. "2 people", "3 people", "4-8 people"
  price?: string; // formatted price like "$115 per person"
};

export type StaticPackage = {
  id: string;
  category: string;
  title: string;
  subtitle?: string;
  availability?: string;
  description: string;
  includes?: string[];
  notes?: string[];
  minimum?: string;
  pricing?: PricingTier[];
  extras?: { label: string; price?: string }[];
  image?: string;
  bookingAvailable?: boolean;
};

// Import images so Vite bundles and rewrites URLs for production (Netlify)
import img1 from '../assets/1.jpeg';
import img2 from '../assets/2.jpeg';
import img7 from '../assets/7.jpeg';
import img8 from '../assets/8.jpeg';
import img9 from '../assets/9.jpeg';
import img11 from '../assets/11.jpeg';
import img12 from '../assets/12.jpeg';
import img14 from '../assets/14.jpeg';
import img16 from '../assets/16.jpeg';
import img19 from '../assets/19.jpeg';
import img21 from '../assets/21.jpeg';
import img22 from '../assets/22.jpeg';
import img24 from '../assets/24.jpeg';
import img26 from '../assets/26.jpeg';
import img30 from '../assets/30.jpeg';
import img31 from '../assets/31.jpeg';
import img32 from '../assets/32.jpeg';
import img33 from '../assets/33.jpeg';
import img34 from '../assets/34.jpeg';

export const PACKAGES: StaticPackage[] = [
  // Aqaba Day Tours
  {
    id: 'aq-scuba',
    category: 'Aqaba Day Tours',
    title: 'Scuba Diving',
    description:
      'Discover the wonders of the underwater world in the beautiful Red Sea. Explore the coral reefs and shipwrecks of the Aqaba Marine Park.',
    includes: ['Transport', 'Entrance to Club Murjan beach club', 'Full equipment hire', 'Instructor/guide', '1 dive'],
    notes: ['PADI courses available on request.'],
    bookingAvailable: true,
    image: img16,
  },

  {
    id: 'aq-neptune',
    category: 'Aqaba Day Tours',
    title: 'Neptune Submarine Boat',
    description:
      "More than a glass-bottomed boat — you'll be submerged in a glass observation bubble to see the reef without getting wet.",
    includes: ['One and a half hour tour on board'],
    bookingAvailable: true,
    image: img21,
  },

  {
    id: 'aq-green',
    category: 'Aqaba Day Tours',
    title: 'Green Creations Handicraft Class',
    availability: 'Sunday–Thursday',
    description:
      'Hands-on handicraft class supporting a local poverty-alleviating project. Make recycled components and enjoy a tea break with artisans.',
    includes: ['Transportation', 'Orientation talk', 'Handicraft class with local artisans', 'Tea', 'The item you design and create'],
    minimum: '3 people',
    bookingAvailable: true,
    image: img24,
  },

  {
    id: 'aq-cooking',
    category: 'Aqaba Day Tours',
    title: 'Cooking Class',
    description:
      'Be invited into a local family home and learn to prepare traditional Jordanian dishes including Maqluba, Mansaf, and Sayyadia.',
    includes: ['Transportation', 'Cooking class', 'Meal'],
    minimum: '3 people',
    bookingAvailable: true,
    image: img12,
  },

  {
    id: 'aq-lunch-cruise',
    category: 'Aqaba Day Tours',
    title: 'Lunch Cruise',
    availability: 'Daily',
    description: 'Enjoy a day on the Red Sea, relax and snorkel, then have lunch on board.',
    includes: ['Snorkeling kit', 'Towel', 'Lunch', 'One soft drink'],
    bookingAvailable: true,
    image: img8,
  },

  {
    id: 'aq-sunset-cruise',
    category: 'Aqaba Day Tours',
    title: 'Sunset Cruise',
    availability: 'Thursdays and Fridays only',
    description: 'Watch the magnificent sunset behind the Sinai mountains while cruising on the Red Sea.',
    includes: ['BBQ dinner', 'One soft drink, beer, or glass of wine'],
    bookingAvailable: true,
    image: img11,
  },

  {
    id: 'aq-city-tour',
    category: 'Aqaba Day Tours',
    title: '4 Hour City Tour',
    description:
      'Guided tour around Aqaba visiting the History Museum, Mamluk Fort, Souq, archaeological sites of Ayla, and more.',
    includes: ['Transportation', 'Driver'],
    bookingAvailable: true,
    image: img14,
  },

  {
    id: 'aq-bird',
    category: 'Aqaba Day Tours',
    title: 'Aqaba Bird Observatory',
    description:
      'Artificial wetland reserve near the Eilat/Aqaba border important for migratory birds. Marked trails and a hide for bird watching.',
    notes: ['Sightings are not guaranteed and species vary by season.'],
    includes: ['Transport', 'Entrance'],
    bookingAvailable: true,
    image: img22,
  },

  {
    id: 'aq-fishing',
    category: 'Aqaba Day Tours',
    title: 'Sport Fishing Private Charters',
    description:
      'Private charters for 4, 6, 8, or 10-hour trips for 2–4 people. Expect tuna, sailfish, trevally and bream (seasonal).',
    includes: ['Water', 'Ice', 'Soft drinks', 'Baits', 'Tackle', 'Reels', 'Rods'],
    bookingAvailable: true,
    image: img30,
  },

  // Dead Sea Day Tours
  {
    id: 'ds-swim-lunch',
    category: 'Dead Sea Day Tours',
    title: 'Dead Sea Swim and Lunch',
    description: 'Enjoy floating and swimming in the Dead Sea followed by lunch.',
    includes: ['Transfers Aqaba → Dead Sea → Aqaba', 'Amman Beach entrance fees', 'Lunch'],
    notes: ['Guests need to bring their own towel and swimwear.'],
    pricing: [
      { group: '2 people', price: '$115 per person' },
      { group: '3 people', price: '$85 per person' },
      { group: '4–8 people', price: '$87 per person' },
    ],
    bookingAvailable: true,
    image: img31,
  },

  {
    id: 'ds-mujib',
    category: 'Dead Sea Day Tours',
    title: 'Dead Sea and Wadi Mujib',
    availability: 'April–October only',
    description: 'Combine Dead Sea relaxation with a visit to Wadi Mujib for hiking and canyoneering.',
    includes: [
      'Transfers Aqaba → Dead Sea → Wadi Mujib → Aqaba',
      'Amman Beach entrance fees',
      'Lunch',
      '2–3 hour self-guided hike up the Siq Trail',
      'Wadi Mujib Reserve entrance fee',
    ],
    pricing: [
      { group: '2 people', price: '$155 per person' },
      { group: '3 people', price: '$120 per person' },
      { group: '4–8 people', price: '$125 per person' },
    ],
    bookingAvailable: true,
    image: img9,
  },

  {
    id: 'ds-biblical',
    category: 'Dead Sea Day Tours',
    title: 'Dead Sea and Biblical Sites',
    description:
      'Relax in the Dead Sea then visit Mount Nebo and Bethany‑beyond‑the‑Jordan and other important sites in the Jordan Valley.',
    includes: ['Transfers Aqaba → Dead Sea → Nebo → Bethany → Aqaba', 'All entrance fees', 'Lunch at Amman Beach (Dead Sea)'],
    pricing: [
      { group: '2 people', price: '$135 per person' },
      { group: '3–8 people', price: '$105 per person' },
    ],
    extras: [{ label: 'Guide for full day', price: '$225 per group' }],
    bookingAvailable: true,
    image: img19,
  },

  // Petra Day Tours
  {
    id: 'petra-day',
    category: 'Petra Day Tours',
    title: 'Petra (Day Trip)',
    description:
      'Transfers Aqaba → Petra → Aqaba with 1-day entrance, horse ride from gate to Siq (800 m), and a local English-speaking guide for 4 hours.',
    includes: ['Transfers Aqaba → Petra → Aqaba', '1-day entrance fee', 'Horse ride from gate to Siq (800 m)', 'Local English-speaking guide for 4 hours'],
    pricing: [
      { group: '2 people', price: '$175 per person' },
      { group: '3 people', price: '$140 per person' },
      { group: '4–8 people', price: '$130 per person' },
    ],
    extras: [{ label: 'Lunch in Petra', price: '$15 per person' }, { label: 'Other-language guide for full day', price: '$100 per group' }],
    notes: ['Prices valid for visitors spending at least 1 night in Jordan. Day-trip visitors from Eilat pay a $58 per person supplement.'],
    bookingAvailable: true,
    image: img2,
  },

  {
    id: 'petra-night',
    category: 'Petra Day Tours',
    title: 'Petra By Night',
    availability: 'Monday, Wednesday, and Thursday only',
    description:
      'Walk through the Siq to the Treasury by candlelight and enjoy traditional Bedouin music. Pick-up 17:30 — Tour 20:00–22:00.',
    includes: ['Transfer Aqaba → Petra → Aqaba', 'Entrance fee', '2-hour tour of the Siq and Treasury'],
    pricing: [
      { group: '2 people', price: '$100 per person' },
      { group: '3 people', price: '$75 per person' },
      { group: '4–8 people', price: '$70 per person' },
    ],
    bookingAvailable: true,
    image: img1,
  },

  {
    id: 'petra-wadirum',
    category: 'Petra Day Tours',
    title: 'Petra and Wadi Rum in 1 Day',
    description:
      'Transfers Aqaba → Petra → Wadi Rum → Aqaba. Includes Petra and Wadi Rum highlights and a 2-hour jeep tour in Wadi Rum. Lunch in Petra included.',
    includes: ['Transfers Aqaba → Petra → Wadi Rum → Aqaba', 'Petra entrance fee', 'Local English-speaking guide for 4 hours in Petra', 'Lunch in Petra', 'Wadi Rum entrance fee', '2-hour jeep tour in Wadi Rum'],
    bookingAvailable: true,
    image: img7,
  },

  // Wadi Rum Day Tours
  {
    id: 'wr-2hr',
    category: 'Wadi Rum Day Tours',
    title: '2 Hour Jeep Tour',
    description: 'Short jeep tour exploring Wadi Rum with a Bedouin driver.',
    includes: ['Transfers Aqaba → Wadi Rum → Aqaba', 'Wadi Rum Reserve entrance fee', '2-hour jeep tour with Bedouin driver'],
    pricing: [
      { group: '2 people', price: '$85 per person' },
      { group: '3 people', price: '$65 per person' },
      { group: '4–8 people', price: '$50 per person' },
    ],
    bookingAvailable: true,
    image: img33,
  },

  {
    id: 'wr-4hr',
    category: 'Wadi Rum Day Tours',
    title: '4 Hour Jeep Tour',
    description: 'Extended jeep tour with more time to explore the desert.',
    includes: ['Transfers Aqaba → Wadi Rum → Aqaba', 'Wadi Rum Reserve entrance fee', '4-hour jeep tour with Bedouin driver'],
    pricing: [
      { group: '2 people', price: '$100 per person' },
      { group: '3 people', price: '$75 per person' },
      { group: '4–8 people', price: '$60 per person' },
    ],
    bookingAvailable: true,
    image: img26,
  },

  {
    id: 'wr-camel',
    category: 'Wadi Rum Day Tours',
    title: 'Camel Tour (Full Day)',
    description: 'Full-day camel tour with Bedouin guide, supporting jeep, lunch included.',
    includes: ['Transfers Aqaba → Wadi Rum → Aqaba', 'Wadi Rum Reserve entrance fee', 'Full-day camel tour', 'Bedouin guide', 'Supporting Jeep', 'Lunch'],
    pricing: [
      { group: '2 people', price: '$200 per person' },
      { group: '3 people', price: '$170 per person' },
      { group: '4–8 people', price: '$155 per person' },
    ],
    bookingAvailable: true,
    image: img32,
  },

  {
    id: 'wr-overnight',
    category: 'Wadi Rum Day Tours',
    title: 'Half Day + Overnight in Bedouin Camp',
    description: '4-hour jeep tour, authentic Arabian dinner, 1-night stay in a Bedouin camp, and breakfast.',
    includes: ['Transfers Aqaba → Wadi Rum → Aqaba', 'Wadi Rum Reserve entrance fee', '4-hour jeep tour with Bedouin driver', 'Authentic Arabian dinner', '1-night stay in a Bedouin camp', 'Breakfast'],
    pricing: [
      { group: '2 people', price: '$190 per person' },
      { group: '3 people', price: '$160 per person' },
      { group: '4–8 people', price: '$155 per person' },
    ],
    bookingAvailable: true,
    image: img34,
  },
];

export default PACKAGES;
